import React, { useEffect } from 'react';
import { Row, Col, Label, Input, FormFeedback } from 'reactstrap';
import CustomSelect from './CustomSelect';
import { FormattedMessage, injectIntl } from 'react-intl';
import { getAPI } from '../API';
import CustomSelectAsync from './CustomSelectAsync';
import countryList from "react-select-country-list";
import BlockUi from 'react-block-ui';
import { getProfileCreationConfig } from './ReferenceDataFunctions';
import { handleNotification } from './Notification';


const ProfileInfoSection = ({ intl, voucher, handleChange, handleSelect, updateVoucherData, hideTitle = false, fiscalNumberRequired = false }) => {
    const [isGenericVat, setIsGenericVat] = React.useState(false);
    const [fieldsConfig, setFieldsConfig] = React.useState([]);
    const [invalidFields, setInvalidFields] = React.useState({});
    const [block, setBlock] = React.useState(false);
    const [error, setError] = React.useState([]);


    useEffect(() => {      
        getProfileValidations();
    }, []);
        
    const getProfileValidations = () => {
        setBlock(true);

        getProfileCreationConfig(({ data, fieldsConfig, error }) => {
            if (error) {
                setError([{ message: error.message, stack: error.stack, messageType: 'danger' }]);
                setBlock(false);
                return;
            }
            if (data && data.errors && data.errors.length > 0) {
                handleNotification(data);
            }

            setFieldsConfig(fieldsConfig);
            setBlock(false);
        });
    }

    const loadClients = (name, cb) => {
        let options = [];
        if (name.length === 0 || name.length > 2) {
            getAPI(result => {
                const { data } = result;
                if (data && data.response && data.response.length > 0) {
                    data.response.forEach(resp => {
                        resp.value = resp.id;
                        resp.label = `${resp.firstName} ${resp.lastName}${resp.email ? ` (${resp.email})` : ``}${resp.phone ? ` (${intl.formatMessage({ id: 'AddVoucher.Phone' })}: ${resp.phone})` : ``} ${resp.fiscalNumber ? ` (${intl.formatMessage({ id: 'AddVoucher.CustomerNif' })}: ${resp.fiscalNumber})` : ``}`
                    });
                    options = data.response;
                }
                cb(options);
            }, `/api/Voucher/Profile/v1/Profile?pageIndex=0&pageSize=10&openToSearch=true${name ? `&filterToSearch=${name}` : ``}`);
        }
        else {
            cb(options);
        }
    }

    const handleSelectProfile = (profile) => {
        const voucherCopy = { ...voucher };

        if (profile) {
            voucherCopy.customerFirstName = profile.firstName;
            voucherCopy.customerLastName = profile.lastName;
            voucherCopy.customerMail = profile.email;
            voucherCopy.customerNif = profile.fiscalNumber;
            voucherCopy.customerPhone = profile.phone;
            voucherCopy.customerCountryCode = profile.countryCode;
            voucherCopy.customerPostCode = profile.postCode;
            voucherCopy.customerCity = profile.city;
            voucherCopy.customerAddress = profile.address;
        }
        else {
            voucherCopy.customerFirstName = null;
            voucherCopy.customerLastName = null;
            voucherCopy.customerMail = null;
            voucherCopy.customerNif = null;
            voucherCopy.customerPhone = null;
            voucherCopy.customerCountryCode = null;
            voucherCopy.customerPostCode = null;
            voucherCopy.customerCity = null;
            voucherCopy.customerAddress = null;
        }
        
        setIsGenericVat(voucherCopy.customerNif === '999999990');
        updateVoucherData(voucherCopy);
    }

    const handleFinalConsumerVat = (e) => {
        const { checked } = e.target;

        setIsGenericVat(checked);

        handleChange({ target: { name: 'customerNif', value: checked ? '999999990' : '' } });
    }

    const fieldKeyMap = {
        customerFirstName: 'firstName',
        customerLastName: 'lastName',
        customerMail: 'email',
        customerNif: 'fiscalNumber',
        customerPhone: 'phone',
        customerCountryCode: 'countryCode',
        customerPostCode: 'postCode',
        customerCity: 'city',
        customerAddress: 'address',
    };

    const handleFieldChange = (evt) => {
        if (evt.target) {
            const { name, value } = evt.target;
            const fieldKey = fieldKeyMap[name];
            const fieldConfig = fieldKey && fieldsConfig.find(f => f.fieldKey === fieldKey);

            let isInvalid = false;

            if (fieldConfig) {
                if (fieldConfig.regex && value) {
                    try {
                        const regex = new RegExp(fieldConfig.regex);
                        isInvalid = !regex.test(value);
                    } catch (e) {
                        // regex inválido — ignorar validação
                    }
                }
            }

            if (fieldKey) {
                setInvalidFields(prev => ({ ...prev, [fieldKey]: isInvalid }));
            }
            handleChange(evt);
        }
    };

    const isFieldRequired = (fieldKey) => {
        return fieldsConfig?.find(f => f.fieldKey === fieldKey)?.isRequired ?? invalidFields[fieldKey];
    };
    
    return (
        <BlockUi tag="div" blocking={block}>
            {hideTitle ? null :
                <>
                    <Row className="mt-4">
                        <Col>
                            <h5>
                                <FormattedMessage id="AddVoucher.ClientInfo" />
                            </h5>
                        </Col>
                    </Row>
                    <hr className="mb-3 mt-0" />
                </>
            }
            <Row className="mb-4">
                <Col className="col-2">
                    <FormattedMessage id="AddVoucher.SearchClients" />
                </Col>
                <Col className="col-10">
                    <CustomSelectAsync
                        cacheOptions
                        loadOptions={loadClients}
                        defaultOptions
                        onChange={handleSelectProfile}
                    />                               
                </Col>
            </Row>
            
            <Row className="mb-2">
                <Col className="col-2">
                    <Label className={isFieldRequired('firstName') ? 'required' : ''}>
                        <FormattedMessage id="AddVoucher.CustomerFirstName" />
                    </Label>
                </Col>
                <Col className="col-4">
                    <Input 
                        type="text" 
                        value={voucher?.customerFirstName || ''} 
                        name="customerFirstName" 
                        onChange={handleFieldChange} 
                        placeholder={intl.formatMessage({ id: 'AddVoucher.CustomerFirstName' })} 
                        required={isFieldRequired('firstName')}
                        invalid={!!invalidFields.firstName}
                    />
                    <FormFeedback><FormattedMessage id="generic.InvalidField" /></FormFeedback>
                </Col>
                <Col className="col-2">
                    <Label className={isFieldRequired('lastName') ? 'required' : ''}>
                        <FormattedMessage id="AddVoucher.CustomerLastName" />
                    </Label>
                </Col>
                <Col className="col-4">
                    <Input 
                        type="text" 
                        value={voucher?.customerLastName || ''} 
                        name="customerLastName" 
                        onChange={handleFieldChange} 
                        placeholder={intl.formatMessage({ id: 'AddVoucher.CustomerLastName' })}
                        required={isFieldRequired('lastName')}
                        invalid={!!invalidFields.lastName}
                    />
                    <FormFeedback><FormattedMessage id="generic.InvalidField" /></FormFeedback>
                </Col>
            </Row>
            <Row className="mb-2">
                <Col className="col-2">
                    <Label className={isFieldRequired('email') ? 'required' : ''}>
                        <FormattedMessage id="AddVoucher.CustomerMail" />
                    </Label>
                </Col>
                <Col className="col-4">
                    <Input 
                        type="email" 
                        value={voucher?.customerMail || ''} 
                        name="customerMail" 
                        onChange={handleFieldChange}
                        placeholder={intl.formatMessage({ id: 'AddVoucher.CustomerMail' })} 
                        required={isFieldRequired('email')}
                        invalid={!!invalidFields.email}
                    />
                    <FormFeedback><FormattedMessage id="generic.InvalidField" /></FormFeedback>
                </Col>
                <Col className="col-2">
                    <Label className={(fiscalNumberRequired ?? isFieldRequired('fiscalNumber')) ? 'required' : ''}>
                        <FormattedMessage id="AddVoucher.CustomerNif" />
                    </Label>
                </Col>
                <Col className="col-2">
                    <Input 
                        type="text"
                        value={voucher?.customerNif || ''} 
                        name="customerNif" 
                        onChange={handleFieldChange}
                        placeholder={intl.formatMessage({ id: 'AddVoucher.CustomerNif' })} 
                        readOnly={isGenericVat}
                        required={fiscalNumberRequired ?? isFieldRequired('fiscalNumber')}
                        invalid={!!invalidFields.fiscalNumber}
                    />
                    <FormFeedback><FormattedMessage id="generic.InvalidField" /></FormFeedback>
                </Col>

                <Col className="col-2 d-flex align-items-center">
                    <Input
                        type="checkbox" 
                        name="isGenericVat"
                        className="mb-2"
                        checked={isGenericVat}
                        onChange={handleFinalConsumerVat}
                    />
                    <Label className="mb-1"><FormattedMessage id="AddVoucher.FinalConsumer" /></Label>
                </Col>
            </Row>
            <Row className="mb-2">
                <Col className="col-2">
                    <Label className={isFieldRequired('phone') ? 'required' : ''}>
                        <FormattedMessage id="AddVoucher.Phone" />
                    </Label>
                </Col>
                <Col className="col-4">
                    <Input 
                        type="text" 
                        name="customerPhone"
                        value={voucher?.customerPhone || ''}
                        onChange={handleFieldChange}
                        placeholder={intl.formatMessage({ id: 'AddVoucher.Phone' })}
                        required={isFieldRequired('phone')}
                        invalid={!!invalidFields.phone}
                    />
                    <FormFeedback><FormattedMessage id="generic.InvalidField" /></FormFeedback>
                </Col>
                <Col className="col-2">
                    <Label className={isFieldRequired('countryCode') ? 'required' : ''}>
                        <FormattedMessage id="AddVoucher.CustomerCountryCode" />
                    </Label>
                </Col>
                <Col sm={4}>
                    <CustomSelect
                        options={countryList().getData()}
                        name="customerCountryCode"
                        isClearable 
                        isSearchable
                        placeholder={<FormattedMessage id="AddVoucher.CustomerCountryCode" />}
                        onChange={handleSelect}
                        value={voucher?.customerCountryCode ? countryList().getData().find(el => el.value === voucher.customerCountryCode) : null}
                        required={isFieldRequired('countryCode')}
                        invalid={!!invalidFields.countryCode}
                    />
                    <FormFeedback><FormattedMessage id="generic.InvalidField" /></FormFeedback>
                </Col>
            </Row>

            <Row className="mb-2">
                <Col className="col-2">
                    <Label className={isFieldRequired('postCode') ? 'required' : ''}>
                        <FormattedMessage id="AddVoucher.CustomerPostCode" />
                    </Label>
                </Col>
                <Col className="col-4">
                    <Input 
                        type="text" 
                        name="customerPostCode"
                        value={voucher?.customerPostCode || ''}
                        onChange={handleFieldChange}
                        placeholder={intl.formatMessage({ id: 'AddVoucher.CustomerPostCode' })}
                        required={isFieldRequired('postCode')}
                        invalid={!!invalidFields.postCode}
                    />
                    <FormFeedback><FormattedMessage id="generic.InvalidField" /></FormFeedback>
                </Col>
                <Col className="col-2">
                    <Label className={isFieldRequired('city') ? 'required' : ''}>
                        <FormattedMessage id="AddVoucher.CustomerCity" />
                    </Label>
                </Col>
                <Col className="col-4">
                    <Input 
                        type="text" 
                        name="customerCity"
                        value={voucher?.customerCity || ''}
                        onChange={handleFieldChange}
                        placeholder={intl.formatMessage({ id: 'AddVoucher.CustomerCity' })}
                        required={isFieldRequired('city')}
                        invalid={!!invalidFields.city}
                    />
                    <FormFeedback><FormattedMessage id="generic.InvalidField" /></FormFeedback>
                </Col>
            </Row>

            <Row className="mb-2">
                    <Col className="col-2">
                        <Label className={isFieldRequired('address') ? 'required' : ''}>
                            <FormattedMessage id="AddVoucher.Address" />
                        </Label>
                    </Col>
                    <Col className="col-10">
                        <Input
                            type="text" 
                            name="customerAddress"
                            value={voucher?.customerAddress || ''}
                            onChange={handleFieldChange}
                            placeholder={intl.formatMessage({ id: 'AddVoucher.Address' })}
                            required={isFieldRequired('address')}
                            invalid={!!invalidFields.address}
                        />           
                        <FormFeedback><FormattedMessage id="generic.InvalidField" /></FormFeedback>
                    </Col>
                </Row>
        </BlockUi>
    );
};

export default injectIntl(ProfileInfoSection);
